import React, { useEffect, useState } from "react";
import { Pagination } from "antd";
import { format, parseISO, isValid } from "date-fns";
import {
  AlertCircle,
  CreditCard,
  Eye,
  Info,
  MapPin,
  Pencil,
  RotateCcw,
  Search,
  User,
  Users,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../layout/index";
import { toast } from "react-toastify";
import { APP_ROUTES } from "../../constants/approutes";

const getInitials = (firstName, lastName) => {
  const f = (firstName || "").charAt(0);
  const l = (lastName || "").charAt(0);
  return (f + l).toUpperCase() || "?";
};

const getPatientDisplayId = (patient) =>
  patient?.patientCode || patient?.patientId || patient?.id || "—";

const isFemale = (gender) =>
  String(gender || "").toLowerCase().startsWith("f");

const formatDob = (value) => {
  if (!value) return null;
  try {
    const parsed = value.includes("T") ? parseISO(value) : new Date(value);
    return isValid(parsed) ? format(parsed, "dd MMM yyyy") : String(value);
  } catch {
    return String(value);
  }
};

const formatLastUpdated = (value) => {
  if (!value) return format(new Date(), "dd MMM yyyy");
  try {
    const parsed = value.includes("T") ? parseISO(value) : new Date(value);
    return isValid(parsed) ? format(parsed, "dd MMM yyyy") : format(new Date(), "dd MMM yyyy");
  } catch {
    return format(new Date(), "dd MMM yyyy");
  }
};

const SectionHeading = ({ title }) => (
  <div className="flex items-center gap-2.5 mb-2.5 mt-0">
    <h4 className="text-[13px] font-bold text-[#0D5C4F] mb-0 shrink-0 capitalize">
      {title}
    </h4>
    <div className="flex-1 h-px bg-slate-200" />
  </div>
);

const DetailField = ({ label, children }) => (
  <div>
    <p className="text-[12px] text-slate-500 mb-0.5">{label}</p>
    {children}
  </div>
);

const getAddressPrimaryLine = (addr) => {
  const line = addr?.address?.trim() || "";
  const landmark = addr?.landmark?.trim() || "";
  if (line && landmark) return `${line}, ${landmark}`;
  return line || landmark || null;
};

const formatAddressSubline = (addr) => {
  const cityState = [addr?.city, addr?.state].filter(Boolean).join(", ");
  const pinPart = addr?.pincode || addr?.pinCode ? `Pin: ${addr.pincode || addr.pinCode}` : null;
  return [cityState, pinPart].filter(Boolean).join(" · ");
};

const AddressCard = ({ primaryLine, subline }) => (
  <div className="rounded-lg bg-[#F4F7F9] border border-slate-100 px-3 py-1.5 flex gap-2.5">
    <div className="w-8 h-8 rounded-md bg-[#E0F2F1] flex items-center justify-center shrink-0">
      <MapPin size={15} className="text-[#0D5C4F]" />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] text-slate-500 mb-0.5">Address</p>
      {primaryLine ? (
        <>
          <p className="text-[13px] font-normal text-slate-800 mb-2 mt-2 ">{primaryLine}</p>
          {subline ? <p className="text-[11px] text-slate-500">{subline}</p> : null}
        </>
      ) : (
        <p className="text-[13px] text-slate-400 italic">Not provided</p>
      )}
    </div>
  </div>
);

const PatientList = () => {
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const [patients, setPatients] = useState([]);
  const [patientAddress, setPatientsAddress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchFirstName, setSearchFirstName] = useState("");
  const [searchMobileNumber, setSearchMobileNumber] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  const token = localStorage.getItem("auth_token");
  const tenantId = localStorage.getItem("tenantId");

  useEffect(() => {
    fetchList({ resetPage: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPageNumber(0);
      fetchList({ resetPage: true });
    }, 500);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchFirstName, searchMobileNumber]);

  useEffect(() => {
    fetchList({ resetPage: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNumber, pageSize]);

  const fetchList = async ({ resetPage = false } = {}) => {
    setLoading(true);
    const nextPage = resetPage ? 0 : pageNumber;
    const params = new URLSearchParams();
    params.set("tenantId", tenantId || "");
    params.set("pageNumber", String(nextPage));
    params.set("pageSize", String(pageSize));

    const trimmedFirstName = searchFirstName?.trim();
    const trimmedMobileNumber = searchMobileNumber?.trim();

    if (trimmedFirstName) params.set("firstName", trimmedFirstName);
    if (trimmedMobileNumber) params.set("mobileNumber", trimmedMobileNumber);

    try {
      const res = await fetch(
        `${API_URL}patient/search/paged?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            tenantId: tenantId,
          },
        }
      );

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const data = await res.json();
      const items = Array.isArray(data?.data)
        ? data
        : data?.content || data?.items || [];
        console.log(data,"erdtfyguhijo")
      setPatients(items || []);
      setTotalElements(Number(data?.totalElements || items?.length || 0));
      if (resetPage) setPageNumber(0);
    } catch (error) {
      console.error("Failed to fetch patient data:", error);
      toast.error("Failed to fetch patient data");
      setPatients([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchFirstName("");
    setSearchMobileNumber("");
    setPageNumber(0);
    fetchList({ resetPage: true });
  };

  useEffect(() => {
    if (focusedInput) {
      const element = document.getElementById(focusedInput);
      if (element) {
        element.focus();
        const length = element.value.length;
        element.setSelectionRange(length, length);
      }
    }
  }, [patients, loading, focusedInput]);

  const fetchPateintAddress = async (patient) => {
    try {
      const res = await fetch(
        `${API_URL}addressbook/getBy?patientId=${patient?.patientCode}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            tenantId: tenantId,
          },
        }
      );
      const data = await res.json();
      setPatientsAddress(Array.isArray(data) ? data : data ? [data] : []);
    } catch (error) {
      console.error("Failed to fetch patient address:", error);
      toast.error("Failed to fetch patient address");
      setPatientsAddress([]);
    }
  };

  useEffect(() => {
    if (selectedPatient) fetchPateintAddress(selectedPatient);
  }, [selectedPatient]);

  const handleViewDetails = (patient) => {
    setSelectedPatient(patient);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPatient(null);
    setPatientsAddress([]);
  };

  const handleEditPatient = () => {
    if (!selectedPatient?.patientCode) {
      toast.info("Patient code not available for edit.");
      return;
    }
    closeModal();
    navigate(APP_ROUTES.PATIENTDETAILS, {
      state: { patientCode: selectedPatient.patientCode },
    });
  };

  const getFullName = (patient) => {
    const parts = [
      patient?.salutation,
      patient?.firstName,
      patient?.middleName,
      patient?.lastName,
    ].filter(Boolean);
    return parts.join(" ") || "—";
  };

  return (
    <DashboardLayout>
      <div className="mt-6 px-2 sm:px-0">
        {/* Page header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="bg-emerald-50 p-2.5 rounded-xl border border-emerald-100">
            <Users size={24} className="text-emerald-600" />
          </div>
          <h1 className="text-xl font-bold text-[#334155]">Patient List</h1>
        </div>

        {/* Search card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 mb-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <div>
              <label className="block text-[13px] font-medium text-slate-600 mb-1.5">
                Patient name
              </label>
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
                <input
                  id="searchFirstName"
                  type="text"
                  value={searchFirstName}
                  onChange={(e) => setSearchFirstName(e.target.value)}
                  onFocus={() => setFocusedInput("searchFirstName")}
                  onBlur={() => setFocusedInput(null)}
                  placeholder="Search by name"
                  className="w-full h-11 pl-9 pr-3 border border-slate-200 rounded-xl bg-slate-50/50 text-sm outline-none focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15"
                />
              </div>
            </div>
            <div>
              <label className="block text-[13px] font-medium text-slate-600 mb-1.5">
                Mobile number
              </label>
              <input
                id="searchMobileNumber"
                type="tel"
                value={searchMobileNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setSearchMobileNumber(value);
                }}
                onFocus={() => setFocusedInput("searchMobileNumber")}
                onBlur={() => setFocusedInput(null)}
                placeholder="Search by mobile (10 digits)"
                className="w-full h-11 px-3 border border-slate-200 rounded-xl bg-slate-50/50 text-sm outline-none focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/15"
              />
            </div>
            <button
              type="button"
              onClick={clearSearch}
              disabled={!searchFirstName && !searchMobileNumber}
              className="h-11 px-4 flex items-center justify-center gap-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors w-full sm:w-auto"
            >
              <RotateCcw size={15} />
              Reset
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-[#EDF3F8] border-b border-[#DDE8F2]">
                  <th className="px-5 py-3.5 text-left text-[12px] font-bold text-[#0B4D76] uppercase tracking-wide">
                    Patient Name / Patient ID
                  </th>
                  <th className="px-5 py-3.5 text-center text-[12px] font-bold text-[#0B4D76] uppercase tracking-wide">
                    Age
                  </th>
                  <th className="px-5 py-3.5 text-left text-[12px] font-bold text-[#0B4D76] uppercase tracking-wide">
                    Mobile Number
                  </th>
                  <th className="px-5 py-3.5 text-center text-[12px] font-bold text-[#0B4D76] uppercase tracking-wide">
                    Gender
                  </th>
                  <th className="px-5 py-3.5 text-center text-[12px] font-bold text-[#0B4D76] uppercase tracking-wide">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="py-16 text-center text-[#004276]">
                      Loading patients…
                    </td>
                  </tr>
                ) : patients.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-16 text-center text-slate-500">
                      No patients found
                    </td>
                  </tr>
                ) : (
                  patients.map((patient, index) => {
                    const female = isFemale(patient.gender);
                    const rowBg = index % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]";

                    return (
                      <tr
                        key={patient.id || patient.patientCode || index}
                        className={`${rowBg} border-b border-[#EEEEEE] hover:bg-[#F0F7FF] transition-colors`}
                      >
                        <td className="px-5 py-4">
                          <div className="flex items-center gap-3 min-w-[200px]">
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center text-[13px] font-bold shrink-0 ${
                                female
                                  ? "bg-pink-100 text-pink-700"
                                  : "bg-sky-100 text-sky-700"
                              }`}
                            >
                              {getInitials(patient.firstName, patient.lastName)}
                            </div>
                            <div className="min-w-0">
                              <p className="text-[14px] font-semibold text-[#111827] truncate">
                                {getFullName(patient)}
                              </p>
                              <p className="text-[12px] text-slate-500 mt-0.5 truncate">
                                {getPatientDisplayId(patient)}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-4 text-center">
                          <span className="inline-flex px-3 py-1 rounded-full bg-slate-100 text-[12px] font-medium text-slate-600">
                            {patient.age != null && patient.age !== ""
                              ? `${patient.age} yrs`
                              : "—"}
                          </span>
                        </td>
                        <td className="px-5 py-4 whitespace-nowrap">
                          <span className="text-[14px] text-slate-700">
                            {patient.mobileNumber || "—"}
                          </span>
                        </td>
                        <td className="px-5 py-4 text-center">
                          {patient.gender ? (
                            <span
                              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold capitalize ${
                                female
                                  ? "bg-pink-50 text-pink-700 border border-pink-200"
                                  : "bg-sky-50 text-sky-700 border border-sky-200"
                              }`}
                            >
                              <User size={12} />
                              {patient.gender}
                            </span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-center whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => handleViewDetails(patient)}
                            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-semibold text-white bg-gradient-to-r from-[#004376] to-[#00BA7E] hover:opacity-90 shadow-sm transition-opacity"
                          >
                            <Eye size={14} />
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div className="px-5 py-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-slate-500 mb-0">
              Total: <span className="font-semibold text-slate-700">{totalElements}</span>
            </p>
            <Pagination
              current={pageNumber + 1}
              total={totalElements || patients.length}
              pageSize={pageSize}
              showSizeChanger
              pageSizeOptions={[10, 20, 50]}
              onChange={(page, size) => {
                setPageNumber((page || 1) - 1);
                if (size !== pageSize) setPageSize(size || 20);
              }}
              onShowSizeChange={(_, size) => {
                setPageSize(size || 20);
                setPageNumber(0);
              }}
            />
          </div>
        </div>

        {/* Patient profile modal */}
        {showModal && selectedPatient && (() => {
          const female = isFemale(selectedPatient.gender);
          const fullName = getFullName(selectedPatient);
          const addresses = Array.isArray(patientAddress) ? patientAddress : [];
          const primaryAddr = addresses[0];
          const cityState = [
            primaryAddr?.city || selectedPatient?.city,
            primaryAddr?.state || selectedPatient?.state,
          ]
            .filter(Boolean)
            .join(", ");
          const pin =
            selectedPatient?.pinCode ||
            primaryAddr?.pincode ||
            primaryAddr?.pinCode ||
            null;

          return (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-50 flex items-center justify-center p-4 overflow-y-auto">
              <div className="w-full max-w-[520px] bg-white rounded-2xl shadow-2xl overflow-hidden my-4">
                {/* Gradient header */}
                <div className="bg-gradient-to-r from-[#004376] via-[#006B5E] to-[#00BA7E] px-4 py-3.5 flex items-start justify-between gap-2.5">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-11 h-11 rounded-lg border border-white/25 bg-[#1a4d6e] flex items-center justify-center text-white text-[15px] font-bold shrink-0 shadow-inner">
                      {getInitials(selectedPatient.firstName, selectedPatient.lastName)}
                    </div>
                    <div className="min-w-0 pt-0.5">
                      <h3 className="text-[17px] font-normal text-white leading-snug truncate">
                        {fullName}
                      </h3>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                        {/* <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-black/25 text-[11px] font-medium text-white">
                          <CreditCard size={11} className="opacity-90 shrink-0" />
                          <span className="truncate max-w-[120px]">
                            {getPatientDisplayId(selectedPatient)}
                          </span>
                        </span> */}
                        {cityState && (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-black/25 text-[11px] font-normal text-white">
                            <MapPin size={11} className="opacity-90 shrink-0" />
                            <span className="truncate max-w-[160px]">{cityState}</span>
                          </span>
                        )}
                        {!cityState && pin && (
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-black/25 text-[11px] font-normal text-white">
                            <MapPin size={11} className="opacity-90 shrink-0" />
                            Pin: {pin}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="w-9 h-9 rounded-lg border border-white/50 bg-transparent flex items-center justify-center text-white hover:bg-white/15 shrink-0 mt-0.5"
                    aria-label="Close"
                  >
                    <X size={18} strokeWidth={2.5} />
                  </button>
                </div>

                {/* Body */}
                <div className="px-4 py-3.5 max-h-[65vh] overflow-y-auto">
                  <SectionHeading title="Personal information" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5 mb-3">
                    <DetailField label="Full name">
                      <p className="text-[14px] font-normal text-slate-800">{fullName}</p>
                    </DetailField>
                    <DetailField label="Gender">
                      {selectedPatient.gender ? (
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-normal capitalize ${
                            female
                              ? "bg-pink-50 text-pink-700 border border-pink-200"
                              : "bg-sky-50 text-sky-700 border border-sky-200"
                          }`}
                        >
                          <User size={12} />
                          {selectedPatient.gender}
                        </span>
                      ) : (
                        <p className="text-[14px] text-slate-400 italic">Not provided</p>
                      )}
                    </DetailField>
                    <DetailField label="Age">
                      <p className="text-[14px] font-normal text-slate-800">
                        {selectedPatient.age != null && selectedPatient.age !== ""
                          ? `${selectedPatient.age} years`
                          : "Not provided"}
                      </p>
                    </DetailField>
                    <DetailField label="Date of birth">
                      <p className="text-[14px] font-normal text-slate-800">
                        {formatDob(selectedPatient.dob) || "Not provided"}
                      </p>
                    </DetailField>
                  </div>

                  <SectionHeading title="Contact details" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5 mb-2.5">
                    <DetailField label="Mobile number">
                      <p className="text-[14px] font-normal text-[#0B4D76]">
                        {selectedPatient.mobileNumber || (
                          <span className="text-slate-400 italic font-normal">Not provided</span>
                        )}
                      </p>
                    </DetailField>
                    <DetailField label="Alternate mobile">
                      <p className="text-[14px] text-slate-800">
                        {selectedPatient.alternatemobileNumber ? (
                          selectedPatient.alternatemobileNumber
                        ) : (
                          <span className="text-slate-400 italic">Not provided</span>
                        )}
                      </p>
                    </DetailField>
                    <DetailField label="ABHA number">
                      {selectedPatient.abhaNumber ? (
                        <p className="text-[14px] font-normal text-slate-800">
                          {selectedPatient.abhaNumber}
                        </p>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-[12px] font-normal text-amber-800">
                          <AlertCircle size={12} />
                          Not Linked
                        </span>
                      )}
                    </DetailField>
                    <DetailField label="Pin code">
                      <p className="text-[14px] font-normal text-slate-800">
                        {pin || <span className="text-slate-400 italic font-normal">Not provided</span>}
                      </p>
                    </DetailField>
                  </div>

                  <div className="space-y-2">
                    {addresses.length > 0 ? (
                      addresses.map((addr, index) => (
                        <AddressCard
                          key={addr.id || `addr-${index}`}
                          primaryLine={getAddressPrimaryLine(addr)}
                          subline={formatAddressSubline(addr)}
                        />
                      ))
                    ) : (
                      <AddressCard
                        primaryLine={selectedPatient?.address || null}
                        subline={formatAddressSubline({
                          city: selectedPatient?.city,
                          state: selectedPatient?.state,
                          pincode: selectedPatient?.pinCode,
                        })}
                      />
                    )}
                  </div>
                </div>

                {/* Footer */}
                <div className="px-4 py-3 bg-[#F8FAFC] border-t border-slate-200 flex items-center justify-end">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-[#004376] to-[#00BA7E] hover:opacity-90 shadow-sm transition-opacity"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </DashboardLayout>
  );
};

export default PatientList;
